import { Card, Icon } from 'antd';
import React, { useMemo } from 'react';
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import LoadingIndicator from '../../components/shared/LoadingIndicator/LoadingIndicator';
import SeoScoreProgressChart from './SeoScoreProgressChart';
import { getSeoScoreProgressChartData, groupOverviewsByUrl } from '../utils';
import useFetchMultipleFilteredOverviews from '../hooks/useFetchMultipleFilteredOverviews';
import { descriptionDateFormat } from '../settings';
import useGetOverviewsWithLastReport from '../hooks/useGetOverviewsWithLastReport';
import { addUrlSchemeIfMissing } from '../../shared/utils';

type Props = {
    websiteUrl: string;
    competitors: string[];
    customDescription?: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
};

export default function SeoScoreProgressCard({ websiteUrl, competitors, customDescription, startDate, endDate }: Props) {
    const navigate = useNavigate();

    const {
        analysisOverviews: mainUrlOverviews,
        lastSavedReportOverview,
        isFetchingAnalysisOverviews: isFetchingMainUrlOverviews,
    } = useGetOverviewsWithLastReport({ url: websiteUrl, startDate, endDate });

    // reports are saved under the full URL, so domain competitors need the scheme to match them
    const competitorUrls = useMemo(() => competitors.map(addUrlSchemeIfMissing), [competitors]);

    const { data: groupedCompetitorOverviews, isLoading: isLoadingCompetitorOverviews } = useFetchMultipleFilteredOverviews({
        urls: competitorUrls,
        startDate,
        endDate,
    });

    const hasAllOverviews = !!mainUrlOverviews && !isLoadingCompetitorOverviews;

    const groupedOverviews = useMemo(
        () => (hasAllOverviews ? { ...groupOverviewsByUrl(mainUrlOverviews, websiteUrl), ...groupedCompetitorOverviews } : {}),
        [groupedCompetitorOverviews, hasAllOverviews, mainUrlOverviews, websiteUrl]
    );

    const chartData = useMemo(() => (hasAllOverviews ? getSeoScoreProgressChartData(groupedOverviews) : []), [groupedOverviews, hasAllOverviews]);

    const isLoading = isFetchingMainUrlOverviews || isLoadingCompetitorOverviews;
    return (
        <div className="dashboard-seo-score-progress-box">
            <Card
                data-cy="seo-score-progress-for-archived-reports"
                style={{ minHeight: '520px' }}
                title={
                    <div className="ant-card-meta-detail">
                        <div className="ant-card-meta-title">SEO Score Progress</div>
                        {!customDescription && (
                            <div className="ant-card-meta-description">{moment(startDate).startOf('month').format(descriptionDateFormat)}</div>
                        )}
                    </div>
                }
                extra={
                    <div className="seo-score-progress-links flexbox">
                        <div
                            className="card-link flexbox align-center"
                            onClick={() => navigate('/dashboard/settings/competitors')}
                        >
                            <Icon type={competitors.length > 0 ? 'edit' : 'plus-circle'} />
                            <span>{competitors.length > 0 ? 'Edit' : 'Add'} competitors</span>
                        </div>
                        {lastSavedReportOverview && (
                            <div
                                className="card-link flexbox align-center"
                                onClick={() => navigate(`/analysis/report?guid=${lastSavedReportOverview.guid}&url=${websiteUrl}`)}
                            >
                                <span>View report</span>
                                <Icon type="right" />
                            </div>
                        )}
                    </div>
                }
            >
                {isLoading && <LoadingIndicator />}
                {!isLoading && (
                    <SeoScoreProgressChart
                        websiteUrl={websiteUrl}
                        competitors={competitorUrls}
                        chartData={chartData}
                    />
                )}
            </Card>
        </div>
    );
}
